import { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import Spinner from '../../../Plateforme/Atoms/Spinner';
import { supabase } from '../../../lib/supabaseClient';
import { generateFD100KVDataURL } from '../../Utils/generateFD100KV';
import * as api from '../../../api';

const Container = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    padding: 20px;
`;

const PageTitle = styled.h1`
    text-align: center;
    font-size: 32px;
    font-weight: 800;
    margin: 40px 0 10px 0;
`;

const Subtitle = styled.p`
    text-align: center;
    color: #666;
    margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
    font-size: 22px;
    font-weight: 700;
    margin: 40px 0 20px 0;
    padding-bottom: 10px;
    border-bottom: 2px solid #1833bd;
    color: #1833bd;
`;

const GenerateAllBtn = styled.button`
    display: block;
    margin: 0 auto 30px;
    padding: 12px 32px;
    background: #1833bd;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(600px, 1fr));
    gap: 24px;
`;

const Card = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 20px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .name {
        font-weight: 700;
        font-size: 16px;
        color: #333;
    }

    .type {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        padding: 2px 8px;
        border-radius: 4px;
        background: #e3f2fd;
        color: #1976d2;
    }
`;

const Badge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    background: ${p => p.$color || '#ff9800'};
    color: white;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    margin-left: 8px;
`;

const KVRow = styled.div`
    display: flex;
    gap: 16px;
`;

const KVBox = styled.div`
    flex: 1;
    text-align: center;

    .label {
        font-size: 12px;
        font-weight: 700;
        color: ${p => p.$type === 'before' ? '#b71c1c' : '#2e7d32'};
        text-transform: uppercase;
        margin-bottom: 8px;
        letter-spacing: 0.5px;
    }

    img {
        width: 100%;
        border-radius: 6px;
        border: 1px solid #eee;
    }
`;

const OverrideInfo = styled.div`
    margin-top: 10px;
    padding: 6px 12px;
    background: #f5f5f5;
    border-radius: 6px;
    font-size: 12px;
    color: #666;
    text-align: center;
`;

const GenerateBtn = styled.button`
    margin-top: 12px;
    padding: 8px 16px;
    border: 1px solid #1833bd;
    background: white;
    color: #1833bd;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    width: 100%;
    transition: all 0.2s;

    &:hover { background: #1833bd; color: white; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TextEditsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;

    th, td {
        padding: 10px 14px;
        text-align: left;
        border-bottom: 1px solid #eee;
        font-size: 13px;
    }

    th {
        font-weight: 700;
        color: #333;
        background: #fafafa;
        font-size: 12px;
        text-transform: uppercase;
    }

    td.before {
        color: #b71c1c;
        font-family: monospace;
        white-space: pre-wrap;
    }

    td.after {
        color: #2e7d32;
        font-family: monospace;
        white-space: pre-wrap;
    }

    td.reason {
        color: #666;
        font-style: italic;
    }

    tr:hover { background: #fafafa; }
`;

// Must match the overrides in generateFD100KV.js
const LOGO_OVERRIDES = {
    'ecole camondo': { offsetY: -15 },
    'carpenter workshop gallery': { scale: 2.5 },
    'oscar ono': { scale: 0.5 },
    'manufacture de tapis de bourgogne': { scale: 1.8 },
    'thg': { scale: 0.55 },
    'versant': { scale: 0.6 },
    'cinna': { scale: 0.75 },
    'trone': { scale: 0.6 },
    'casino 2000': { scale: 1.6 },
    'issé': { scale: 0.55 },
    'isse': { scale: 0.55 },
    'ceylon': { scale: 2.0 },
    'ceylan': { scale: 2.0 },
    'benetiere': { scale: 2.0 },
    'bénétière': { scale: 2.0 },
    'marco roy': { scale: 2.2 },
};

// Text/wording edits that affect layout (line breaks, overflow fixes)
// searchField: which field to match against (projectName or designerName)
// searchText: lowercase text to find the matching project (from the wordingMapping "from" value)
const TEXT_EDITS = [
    {
        label: 'Montblanc W&W',
        before: 'Montblanc\nWatches & Wonders exhibition, Switzerland',
        after: 'Montblanc Watches &\nWonders Exhibition, Switzerland',
        reason: 'Country text overflow',
        searchField: 'projectName',
        searchText: 'montblanc',
        useDefinitiveName: true,
    },
    {
        label: 'Bureau Des Mésarchitectures',
        before: 'Bureau Des Mésarchitectures // Didier Fiúza Faustino',
        after: 'Bureau Des Mésarchitectures //\nDidier Fiúza Faustino',
        reason: 'Name too long',
        searchField: 'designerName',
        searchText: 'bureau des mésarchitectures',
    },
    {
        label: 'Neophore',
        before: 'Neophore: Transporting Light',
        after: 'Neophore: Transporting\nLight',
        reason: 'Text overflow',
        searchField: 'projectName',
        searchText: 'neophore',
    },
    {
        label: '100 Colors Of Skirts',
        before: '100 Colors Of Skirts Emmanuelle Moureaux X Cfcl',
        after: '100 Colors Of Skirts Emmanuelle\nMoureaux X Cfcl',
        reason: 'Text overflow',
        searchField: 'projectName',
        searchText: '100 colors of skirts',
    },
    {
        label: 'Studio Delphine Maumot',
        before: 'Studio Delphine Maumot Interior Design & Collectible Design',
        after: 'Studio Delphine Maumot Interior\nDesign & Collectible Design',
        reason: 'Name too long',
        searchField: 'designerName',
        searchText: 'studio delphine maumot',
    },
    {
        label: 'Pierre Yves Rochon',
        before: 'Pierre Yves Rochon / Anne Cecile Thirifays',
        after: 'Pierre Yves Rochon /\nAnne Cecile Thirifays',
        reason: 'Name too long',
        searchField: 'designerName',
        searchText: 'pierre yves rochon',
    },
    {
        label: 'La Pergola',
        before: 'La Pergola, Rome Cavalieri, Waldorf Astoria',
        after: 'La Pergola, Rome Cavalieri,\nWaldorf Astoria',
        reason: 'Text overflow',
        searchField: 'projectName',
        searchText: 'la pergola',
    },
    {
        label: 'France Pavilion',
        before: 'France Pavilion – Expo 2025 Osaka',
        after: 'France Pavilion – Expo\n2025 Osaka',
        reason: 'Text overflow',
        searchField: 'projectName',
        searchText: 'france pavili',
    },
    {
        label: 'La Casa Imperfetta',
        before: 'La Casa Imperfetta – Triennale Milano Museum',
        after: 'La Casa Imperfetta – Triennale\nMilano Museum',
        reason: 'Text overflow',
        searchField: 'projectName',
        searchText: 'la casa imperfetta',
    },
    {
        label: 'Making NUNO Textiles',
        before: 'Making NUNO Textiles, Art Tower Mito',
        after: 'Making NUNO Textiles,\nArt Tower Mito',
        reason: 'Text overflow',
        searchField: 'projectName',
        searchText: 'making nuno',
    },
    {
        label: 'APPRT2',
        before: 'APPRT2 Caroline Grondin & Guillaume Avarguez',
        after: 'APPRT2\nCaroline Grondin &\nGuillaume Avarguez',
        reason: 'Name too long',
        searchField: 'designerName',
        searchText: 'apprt2',
        useDefinitiveName: true,
    },
];

const EVENT_ID = '67590da5499bcc995c210b40';

const WINNER_PROJECT_IDS = [
    '67a332e21b103d938bafb0dc', '67a0b8331b103d938bafa954', '67aa1011740d844a3d633e93',
    '6829daa5754f51df2ef9141c', '67e6c18043d5609350e381a8', '67b5e6146890f1a147192905',
    '67be4a58b1ba28c3144aeb73', '67bf2330b1ba28c3144af8d2', '67c090dcdb48cb16a13c5b43',
    '677268097ec5d90c89d6fe63', '67c6b416db48cb16a13c73c7', '67ab652c07736060460b3b75',
    '68348986754f51df2ef9b87c', '67d15108d95ccf24eaf191df', '67d7d38c43d5609350e242a1',
    '67d82d2243d5609350e24c5e', '662adc507159e5b1b1da7c75', '67d8718c43d5609350e25c38',
    '67d1977243d5609350e218f2', '682b5c5f754f51df2ef93bbc', '67da910a43d5609350e274d7',
    '67e1864b43d5609350e2baeb', '67daebf343d5609350e27a9e', '67dd585043d5609350e28e57',
    '67dfeb0e43d5609350e2a360', '67e1452a43d5609350e2b066', '678675e2de3ed4a7001ab532',
    '67e2d05943d5609350e2cf97', '67e3e1cb43d5609350e304e5', '67e3e81543d5609350e308de',
    '67e3f02643d5609350e30a0a', '67e42d7043d5609350e32539', '67e42e5543d5609350e32570',
    '67e45c9843d5609350e329c0', '67e529f743d5609350e33a8c', '67e53aab43d5609350e340c3',
    '67e56ab043d5609350e3490f', '67e5764d43d5609350e34cb5', '67e57f5143d5609350e3519a',
    '67e5864c43d5609350e353a3', '67e58da443d5609350e3561d', '67e58ebc43d5609350e35665',
    '67e5ab6b43d5609350e35905', '67e6828d43d5609350e36b6a', '67e6acaf43d5609350e37832',
    '67e51ddb43d5609350e33667', '67e6c0a843d5609350e380cf', '67e6d53a43d5609350e38725',
    '67e6dfff43d5609350e38900', '67e7003c43d5609350e38bfc', '6834625c754f51df2ef9a4b1',
    '67ea99b76b7945ed1a576bae', '67f90250a0262e085d37088f', '67e1741a43d5609350e2b608',
    '67fe7a6c9167c049fff4e37e', '67ab6ade07736060460b3c16', '6810a343264e7745556acae1',
    '6800f4c59167c049fff51f5d', '6800b8529167c049fff50e47', '6800e76e9167c049fff5197f',
    '6800ef849167c049fff51c6f', '6800fad59167c049fff52224', '680113739167c049fff52d73',
    '680106139167c049fff525af', '680125219167c049fff53405', '680957ab9167c049fff5a7c2',
    '681164d36c8fbfe5e3c91e9e', '68020ef79167c049fff54015', '68025d069167c049fff56132',
    '680b728c9167c049fff5c8f7', '680249249167c049fff55a19', '68024ed89167c049fff55b90',
    '68025b1a9167c049fff56037', '680270379167c049fff566f5', '680274c09167c049fff567c8',
    '680275419167c049fff567de', '68027b389167c049fff56954', '680291609167c049fff56cad',
    '6802a3fc9167c049fff56f5f', '6849787b17dde4ca4c4bf043', '6849435a17dde4ca4c4be251',
    '6807cb759167c049fff58dd4', '6808c3f59167c049fff599c7', '6808c3859167c049fff599b1',
    '680fa4b19167c049fff5deee', '6801e2f49167c049fff53ce1', '680a54a09167c049fff5bbc4',
    '680b94b29167c049fff5cd49', '6810df4a264e7745556ad75d', '680fa3f89167c049fff5deb1',
    '680fa844a71ddf126b77b977', '680fa9f09167c049fff5e027', '6810c9b9264e7745556acf4d',
    '681125b66c8fbfe5e3c91c62', '6818cac19e656d77bc6c72fa', '6818d27e9e656d77bc6c75a3',
    '6818dbdf9e656d77bc6c78a0', '682d97b2754f51df2ef96070', '67fccffaa0262e085d370ddc',
    '680271809167c049fff5674a'
];

function getOverride(name) {
    if (!name) return null;
    return LOGO_OVERRIDES[name.toLowerCase().trim()] || null;
}

function getEditLabel(override) {
    if (!override) return '';
    const parts = [];
    if (override.scale && override.scale < 1) parts.push(`Scaled down (${override.scale}x)`);
    if (override.scale && override.scale > 1) parts.push(`Scaled up (${override.scale}x)`);
    if (override.offsetY && override.offsetY < 0) parts.push(`Raised ${-override.offsetY}px`);
    if (override.offsetY && override.offsetY > 0) parts.push(`Lowered ${override.offsetY}px`);
    return parts.join(' + ');
}

// Helper: get designer display name (same logic as generateFD100KV.js)
function getDesignerName(project, user) {
    if (project.override_designer_name) return project.override_designer_name;
    if (project.candidate_as_person_fd100_25 === "oui") {
        return `${user.hsProfile?.firstname || ''} ${user.hsProfile?.lastname || ''}`.trim();
    }
    return user.hsCompany?.properties?.name || '';
}

// Find a project matching a text edit search (checks raw + definitive names)
function findProjectForTextEdit(edit, allProjects, defNames) {
    return allProjects.find(({ project, user }) => {
        const name = (project.name_en || project.name || '').toLowerCase();
        const designer = getDesignerName(project, user).toLowerCase();
        // Also check edited names from Supabase tracking
        const def = defNames?.[project._id];
        const defName = (def?.projectName || '').toLowerCase();
        const defDesigner = (def?.creatorName || '').toLowerCase();

        if (edit.searchField === 'designerName') {
            return designer.includes(edit.searchText) || defDesigner.includes(edit.searchText);
        }
        return name.includes(edit.searchText) || defName.includes(edit.searchText);
    });
}

const FD100KVLogoReview = () => {
    const [partenaires, setPartenaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [kvPreviews, setKvPreviews] = useState({});
    const [generatingFor, setGeneratingFor] = useState(null);
    const [generatingAll, setGeneratingAll] = useState(false);
    const [allProjectsData, setAllProjectsData] = useState([]);
    const [textKvPreviews, setTextKvPreviews] = useState({}); // idx -> { before, after }
    const [generatingTextFor, setGeneratingTextFor] = useState(null);
    const [generatingAllText, setGeneratingAllText] = useState(false);
    const [definitiveNames, setDefinitiveNames] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await supabase
                    .from('fd100_partenaires')
                    .select('id, name, type, logo_urls, linked_project_ids')
                    .eq('event_id', EVENT_ID)
                    .order('name');

                if (data) setPartenaires(data);

                // Fetch edited data from Supabase (same source as FD100KVTest)
                const { data: trackingData } = await supabase
                    .from('fd100_laureats_tracking')
                    .select('project_id, edited_project_name, edited_creator_name');

                const namesMap = {};
                if (trackingData) {
                    trackingData.forEach(item => {
                        namesMap[item.project_id] = {
                            projectName: item.edited_project_name,
                            creatorName: item.edited_creator_name,
                        };
                    });
                }
                setDefinitiveNames(namesMap);

                const response = await api.getParticipatingUsersWithData({ eventId: EVENT_ID });
                let projects = [];
                response.data.forEach(user => {
                    if (user.participation.data.projets) {
                        user.participation.data.projets.forEach(project_id => {
                            const project = user.company.projects.find(p => p._id === project_id);
                            if (project && project.status === "validé" && WINNER_PROJECT_IDS.includes(project._id)) {
                                projects.push({ project, user });
                            }
                        });
                    }
                });
                setAllProjectsData(projects);
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const generateKVPreview = useCallback(async (partenaire) => {
        const logoUrl = partenaire.logo_urls?.[0];
        if (!logoUrl || !partenaire.linked_project_ids?.length) return;

        const sampleProjectId = partenaire.linked_project_ids.find(id =>
            allProjectsData.some(p => p.project._id === id)
        );
        if (!sampleProjectId) return;
        const projectData = allProjectsData.find(p => p.project._id === sampleProjectId);
        if (!projectData) return;

        setGeneratingFor(partenaire.id);
        try {
            // Use definitive names if available
            const def = definitiveNames?.[sampleProjectId];
            const editedData = def ? { projectName: def.projectName, creatorName: def.creatorName } : null;
            // "Before" = no override (null name), "After" = with override (pass name)
            const [beforeNormal, afterNormal] = await Promise.all([
                generateFD100KVDataURL(projectData, false, 'cover', editedData, logoUrl, null),
                generateFD100KVDataURL(projectData, false, 'cover', editedData, logoUrl, partenaire.name)
            ]);

            setKvPreviews(prev => ({
                ...prev,
                [partenaire.id]: { before: beforeNormal, after: afterNormal }
            }));
        } catch (err) {
            console.error('Error generating KV preview:', err);
        }
        setGeneratingFor(null);
    }, [allProjectsData, definitiveNames]);

    const generateAllPreviews = useCallback(async () => {
        setGeneratingAll(true);
        const overridden = partenaires.filter(p => !!getOverride(p.name) && p.logo_urls?.[0]);
        for (const p of overridden) {
            if (!kvPreviews[p.id]) {
                await generateKVPreview(p);
            }
        }
        setGeneratingAll(false);
    }, [partenaires, kvPreviews, generateKVPreview]);

    // Generate before/after KV for a single text edit
    const generateTextKVPreview = useCallback(async (editIdx) => {
        const edit = TEXT_EDITS[editIdx];
        const projectData = findProjectForTextEdit(edit, allProjectsData, definitiveNames);
        if (!projectData) return;

        // Only pass edited data when the raw name differs from wording mapping input
        let editedData = null;
        if (edit.useDefinitiveName) {
            const def = definitiveNames?.[projectData.project._id];
            if (def) {
                editedData = {
                    projectName: def.projectName,
                    creatorName: def.creatorName
                };
            }
        }

        setGeneratingTextFor(editIdx);
        try {
            const [beforeKV, afterKV] = await Promise.all([
                generateFD100KVDataURL(projectData, false, 'cover', editedData, null, null, true),
                generateFD100KVDataURL(projectData, false, 'cover', editedData, null, null, false)
            ]);
            setTextKvPreviews(prev => ({ ...prev, [editIdx]: { before: beforeKV, after: afterKV } }));
        } catch (err) {
            console.error('Error generating text edit KV:', err);
        }
        setGeneratingTextFor(null);
    }, [allProjectsData, definitiveNames]);

    // Generate all text edit previews
    const generateAllTextPreviews = useCallback(async () => {
        setGeneratingAllText(true);
        for (let i = 0; i < TEXT_EDITS.length; i++) {
            if (!textKvPreviews[i]) {
                await generateTextKVPreview(i);
            }
        }
        setGeneratingAllText(false);
    }, [textKvPreviews, generateTextKVPreview, definitiveNames]);

    const overriddenPartenaires = partenaires.filter(p => !!getOverride(p.name));

    if (loading) {
        return (
            <Container>
                <PageTitle>KV Edits Review</PageTitle>
                <div style={{ textAlign: 'center', padding: '60px' }}><Spinner /></div>
            </Container>
        );
    }

    return (
        <Container>
            <PageTitle>FD100 KV - Edits Review</PageTitle>
            <Subtitle>
                {overriddenPartenaires.length} logo edits | {TEXT_EDITS.length} text edits
            </Subtitle>

            {/* ═══════════════ LOGO EDITS ═══════════════ */}
            <SectionTitle>Logo Size Edits ({overriddenPartenaires.length})</SectionTitle>

            <GenerateAllBtn
                onClick={generateAllPreviews}
                disabled={generatingAll || allProjectsData.length === 0}
            >
                {generatingAll ? 'Generating...' : 'Generate All Before/After Previews'}
            </GenerateAllBtn>

            <Grid>
                {overriddenPartenaires.map(partenaire => {
                    const override = getOverride(partenaire.name);
                    const preview = kvPreviews[partenaire.id];
                    const isGenerating = generatingFor === partenaire.id;
                    const editLabel = getEditLabel(override);

                    return (
                        <Card key={partenaire.id}>
                            <CardHeader>
                                <div>
                                    <span className="name">{partenaire.name}</span>
                                    <Badge $color={override.scale && override.scale < 1 ? '#e65100' : override.scale && override.scale > 1 ? '#1b5e20' : '#1565c0'}>
                                        {editLabel}
                                    </Badge>
                                </div>
                                <span className="type">{partenaire.type}</span>
                            </CardHeader>

                            {preview ? (
                                <KVRow>
                                    <KVBox $type="before">
                                        <div className="label">Before</div>
                                        <img src={preview.before} alt="Before" />
                                    </KVBox>
                                    <KVBox $type="after">
                                        <div className="label">After</div>
                                        <img src={preview.after} alt="After" />
                                    </KVBox>
                                </KVRow>
                            ) : (
                                <GenerateBtn
                                    onClick={() => generateKVPreview(partenaire)}
                                    disabled={isGenerating || allProjectsData.length === 0}
                                >
                                    {isGenerating ? 'Generating...' : 'Generate Before / After'}
                                </GenerateBtn>
                            )}

                            <OverrideInfo>
                                {override.scale ? `scale: ${override.scale}` : ''}
                                {override.scale && override.offsetY ? ' | ' : ''}
                                {override.offsetY ? `offsetY: ${override.offsetY}px` : ''}
                                {' — '}
                                {(partenaire.linked_project_ids || []).length} linked project(s)
                            </OverrideInfo>
                        </Card>
                    );
                })}
            </Grid>

            {/* ═══════════════ TEXT EDITS ═══════════════ */}
            <SectionTitle>Text / Line Break Edits ({TEXT_EDITS.length})</SectionTitle>

            <GenerateAllBtn
                onClick={generateAllTextPreviews}
                disabled={generatingAllText || allProjectsData.length === 0}
            >
                {generatingAllText ? 'Generating...' : 'Generate All Text Edit Previews'}
            </GenerateAllBtn>

            <Grid>
                {TEXT_EDITS.map((edit, idx) => {
                    const preview = textKvPreviews[idx];
                    const isGenerating = generatingTextFor === idx;
                    const matchedProject = findProjectForTextEdit(edit, allProjectsData, definitiveNames);

                    return (
                        <Card key={idx}>
                            <CardHeader>
                                <div>
                                    <span className="name">{edit.label}</span>
                                    <Badge $color="#7b1fa2">{edit.reason}</Badge>
                                </div>
                                <span className="type">{edit.searchField === 'designerName' ? 'Designer name' : 'Project name'}</span>
                            </CardHeader>

                            <TextEditsTable style={{ marginBottom: '12px' }}>
                                <tbody>
                                    <tr>
                                        <td style={{ width: '60px', fontWeight: 700, fontSize: '11px', color: '#b71c1c' }}>BEFORE</td>
                                        <td className="before">{edit.before}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '60px', fontWeight: 700, fontSize: '11px', color: '#2e7d32' }}>AFTER</td>
                                        <td className="after">{edit.after}</td>
                                    </tr>
                                </tbody>
                            </TextEditsTable>

                            {preview ? (
                                <KVRow>
                                    <KVBox $type="before">
                                        <div className="label">Before</div>
                                        <img src={preview.before} alt="Before" />
                                    </KVBox>
                                    <KVBox $type="after">
                                        <div className="label">After</div>
                                        <img src={preview.after} alt="After" />
                                    </KVBox>
                                </KVRow>
                            ) : (
                                <GenerateBtn
                                    onClick={() => generateTextKVPreview(idx)}
                                    disabled={isGenerating || !matchedProject}
                                >
                                    {isGenerating ? 'Generating...' : !matchedProject ? 'No matching project found' : 'Generate Before / After'}
                                </GenerateBtn>
                            )}
                        </Card>
                    );
                })}
            </Grid>
        </Container>
    );
};

export default FD100KVLogoReview;
